/**
 * components/inspection/InspectionStartButton.js
 * «1-3 шатны шалгалт» — шалгалт ЭХЛҮҮЛЭХ товч (дахин ашиглагдах).
 *
 * Урсгал:
 *   1) Товч дарна → шат (I/II/III) + бүтцийн нэгж/хэсэг + ажлын байр сонгоно
 *   2) Шалгалт үүснэ — тухайн нэгжийн БАТЛАГДСАН хуудсын мөрүүд seed хийгдэнэ
 *      (журам 2.3.2/2.4.2/2.5.3; batlagdaagүй бол журмын загварын мөр)
 *   3) Шууд бөглөх дэлгэц (InspectionChecklistForm) модал дотор нээгдэнэ
 *
 * Дуудагдах газрууд:
 *   • /inspection/register — Хяналт шалгалт → Бүртгэл
 *   • шаардлагатай бусад хуудас (жишээ: /me, дашборд)
 *
 * Props:
 *   buttonStyle / buttonLabel — товчны харагдац (эцгийн загварт нийцүүлэх)
 *   defaultStage              — анхдагч шат (үндсэн "I")
 *   onFinished                — шалгалт хаагдсаны дараа (жагсаалт сэргээх)
 */
import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Select,
  AutoComplete,
  Checkbox,
  InputNumber,
  DatePicker,
  Row,
  Col,
  message,
  Alert,
  Tag,
  Button,
  Space,
} from "antd";
// Байршил авах товчны icon
import { EnvironmentOutlined } from "@ant-design/icons";
import moment from "moment";

import {
  xabInspectionApi,
  xabChecklistSheetApi,
  xabBnMonitoringApi,
  xabInspectionDirectiveApi,
  xabBnWorkplaceApi,
} from "src/api/xabApi";
import DepartmentTseh from "src/components/departmentTseh";
import { coversUnit } from "src/components/inspection/monitoringScope";
import DepartmentNegj from "src/components/departmentNegj";
import { filterWorkplaces } from "./workplaceScope";
import DepartmentGroup, {
  groupBelongsToSection,
} from "src/components/departmentGroup";
// Хяналт хийх ажилтныг лавлахаас сонгох (GET /xab/workers, БН-ээр хумигдана)
import WorkerSelect from "src/components/inspection/WorkerSelect";
import InspectionChecklistForm from "./InspectionChecklistForm";
import { useUserContext } from "src/contexts/userContext";
// Гар утасны браузер — модалыг бүтэн дэлгэцээр, талбаруудыг нэг баганаар
import useIsMobile from "src/hooks/useIsMobile";
/* Лавлахад FA6-ийн нэр (`fa-shield-halved`) бичигдсэн байж болно — локал
   Font Awesome нь 5.11 тул буулгаж таниулна, эс бөгөөс icon хоосон гарна. */
import { normalizeFaIcon } from "src/components/hazard";
import {
  allowedStageCodes,
  allowedStages,
  allowedDirectiveLevels,
  DIRECTIVE_STAGE_CODES,
} from "./stageAccess";

/* ── ШАТНЫ ТОВЧНУУД ──────────────────────────────────────────────────
   Хяналтын шатыг УНЖИХ ЖАГСААЛТААР биш, ХУУЧИН ШИГ ӨНГӨТ ТОВЧНУУДААР
   сонгуулна (зөрчлийн маягтын «Үзлэгийн шат»-тай ижил харагдац,
   hazard/InspectionLevelSection.js). Унжих жагсаалт нь нэг товшилт илүү
   бөгөөд эрхтэй шатуудыг нэг харцаар харуулдаггүй байв.
   Form.Item-ийн хүү тул antd `value`/`onChange`-ийг өөрөө өгнө.

   ⚠ ТОВЧНЫ МЭДЭЭЛЭЛ НЬ ЛАВЛАХААС (`xab.inspection_stage`): нэршил, ӨНГӨ,
     ICON, давтамж, ХЭН явуулах нь бүгд тэндээс ирнэ (2026-09-29). Өмнө нь
     өнгө нь ЭНД бичигдсэн байсан тул лавлах дээр өнгө сольсон ч товч
     хуучнаараа гарч, хоёр газар зөрж байв. Доорх зураглал нь ЗӨВХӨН
     нөөц — лавлахад өнгө хоосон мөрд.  */
const STAGE_COLOR = {
  I: "#16A34A",
  II: "#0EA5E9",
  III: "#EA580C",
  IV: "#A32D2D",
  targeted: "#531DAB",
  irregular: "#7C3AED",
  monitoring: "#0F6E56",
};

function StageButtons({ value, onChange, options = [] }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map((o) => {
        // Лавлахын өнгө нь ТЭРГҮҮН; байхгүй бол нөөц зураглал
        const color = o.color || STAGE_COLOR[o.value] || "#64748B";
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange?.(o.value)}
            // Лавлахын «хэн явуулах» — товч дээр багтахгүй тул tooltip-д
            title={o.actor || o.hint || undefined}
            style={{
              flex: "1 1 96px",
              minWidth: 96,
              padding: "10px 6px",
              borderRadius: 10,
              border: "2px solid " + (active ? color : "#E6E9EF"),
              background: active ? color : "#fff",
              color: active ? "#fff" : color,
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1.25,
              cursor: "pointer",
              transition: "all 0.15s",
              boxShadow: active ? `0 2px 8px ${color}55` : "none",
            }}
          >
            {o.icon ? (
              <i
                className={"fas " + normalizeFaIcon(o.icon)}
                style={{ display: "block", fontSize: 15, marginBottom: 3 }}
              />
            ) : null}
            {o.label}
            {o.hint ? (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  opacity: active ? 0.9 : 0.7,
                  marginTop: 2,
                }}
              >
                {o.hint}
              </div>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Товчны нэр — хэрэглэгчид нээлттэй төрлүүдээс автоматаар.
 *
 * Өмнө нь «1-3 шатны шалгалт» гэж ХАТУУ бичигдсэн байсан тул ХАБЭА
 * хэлтсийн мэргэжилтэн (4 дүгээр шат) ч ижил бичигтэй товч хардаг байв —
 * өөрт нь хамаагүй мэт ойлгогдоно.
 */
function autoLabel(codes) {
  const nums = ["I", "II", "III", "IV"].filter((c) => codes.includes(c));
  const parts = [];
  if (nums.length === 4) parts.push("Бүх шатны");
  else if (nums.length === 1) parts.push(`${nums[0]} шатны`);
  else if (nums.length) parts.push(`${nums[0]}-${nums[nums.length - 1]} шатны`);
  if (codes.includes("targeted")) parts.push("зорилтот");
  if (codes.includes("irregular")) parts.push("ээлжит бус");
  return parts.length ? `${parts.join(", ")} шалгалт` : "Шалгалт эхлүүлэх";
}

/* Байршил авч чадаагүй үеийн ЗААВАР. Шалтгаан бүрд ӨӨР алхам —
   «зөвшөөрөл олгоогүй»-г хөтчөөс сэргээнэ, «GPS төхөөрөмжгүй»-г
   сэргээх арга БАЙХГҮЙ (гараар оруулахаас өөр зам алга). Нэг ерөнхий
   заавар өгвөл хэрэглэгч ажиллахгүй алхмыг дэмий давтана. */
const GPS_HELP = {
  denied: {
    title: "Байршил авах зөвшөөрлийг хориглосон байна",
    steps: [
      "Хаягийн мөрний зүүн талын цоожны (эсвэл тохиргооны) дүрс дээр дарна.",
      "«Сайтын тохиргоо» → «Байршил» → «Зөвшөөрөх» болгоно.",
      "Хуудсыг дахин ачаалаад (F5) «Байршил авах» дарна.",
      "⚠ Нэг удаа хориглосныг хөтөч САНАДАГ тул товчийг дахин дарахад " +
        "юу ч болохгүй — заавал тохиргооноос сэргээнэ.",
    ],
  },
  unavailable: {
    title: "Байршил тодорхойлогдсонгүй",
    steps: [
      "Компьютерт GPS төхөөрөмж байдаггүй — хөтөч нь сүлжээгээр " +
        "ойролцоолдог ба дотоод сүлжээнд тэр ажиллахгүй байж болно.",
      "Доороос ГАРААР оруулах эсвэл утаснаасаа шалгалт эхлүүлнэ үү.",
    ],
  },
  timeout: {
    title: "Байршил авахад хугацаа хэтэрлээ",
    steps: [
      "Цонхны дэргэд эсвэл задгай талбайд дахин оролдоно уу.",
      "Дахин бүтэхгүй бол доороос гараар оруулна уу.",
    ],
  },
  insecure: {
    title: "HTTPS хаяг шаардлагатай",
    steps: [
      "Хөтөч нь HTTPS биш хаяг дээр байршил авахыг хориглодог.",
      "https://hse.erdenetmc.mn хаягаар орох эсвэл гараар оруулна уу.",
    ],
  },
  unsupported: {
    title: "Энэ хөтөч байршил дэмжихгүй",
    steps: ["Доороос гараар оруулна уу."],
  },
};

export default function InspectionStartButton({
  buttonStyle,
  buttonLabel,
  defaultStage,
  // Шат нь аль хэдийн сонгогдсон бол (жишээ: зөрчлийн маягтын Үзлэгийн шат)
  // сонгогчийг нуугаад шууд тэр шатаар эхлүүлнэ.
  fixedStage,
  // Эцэг маягтад ЦЕХ/ХЭСЭГ аль хэдийн сонгогдсон бол (зөрчлийн маягт) дахин
  // асуухгүй — өвлөж авна. Утга ирвэл сонгогчид нуугдана.
  bnCode,
  bnName,
  tsehCode,
  tsehName,
  onFinished,
}) {
  const [stages, setStages] = useState([]);
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  /* ── ХЭРЭГЛЭГЧИД ТОХИРСОН ТӨРЛҮҮД ────────────────────────────────────
     Өмнө нь энэ цонх БҮХ 6 төрлийг (I,II,III,IV,зорилтот,ээлжит бус)
     хэн бүхэнд харуулдаг байв. Тиймээс БН-ийн ХАБ ажилтан IV шатны
     хуудсаар шалгалт эхлүүлэх боломжтой байсан — эрхийн цоорхой.
     Одоо components/inspection/stageAccess.js-ийн НЭГ шийдвэрээр шүүнэ.  */
  const { user, isAdmin: isAdminFn } = useUserContext();
  const isAdmin = !!(isAdminFn && isAdminFn());
  const myCodes = React.useMemo(
    () => allowedStageCodes(user, isAdmin),
    [user, isAdmin],
  );
  const myLevels = React.useMemo(
    () => allowedDirectiveLevels(user, isAdmin),
    [user, isAdmin],
  );

  /* ── I·II ШАТААР ХУМИГДСАН БН-ийн ХЭРЭГЛЭГЧ ────────────────────
     Хэрэглэгч (2026-10-29): «1-2 шатны БН дээрх хэрэглэгч хяналт
     шалгалт эхлүүлэх хэсэгт БН хэсэг сонголт 2-г нуух».

     Тэд ЗӨВХӨН өөрийн нэгжид шалгалт хийдэг (I шат — наряд өгөгч ээлж
     бүр, II шат — хэсгийн дарга 14 хоногт) тул нэгжийн сонгогч нь
     сонгох зүйлгүй бөгөөд буруу нэгж заах эрсдэл л нэмнэ.

     ⚠ ХЭМЖСЭН (2026-10-29): эрхээр бодоход 664 хэрэглэгчийн дээд шат
       нь ≤2 — бүлэг 168-ийн 527, бүлэг 167-ийн 156. Бүлэг 166 (цех
       дарга), 179 (БН-ийн ХАБ), 174 (бүрэн эрх)-д ТЭГ. Иймд
       `max(stages) <= 2` нь яг тэр хоёр бүлгийг онилж, дээд шатны
       хэнийг Ч хумихгүй.

     ⚠ Хоосон `[]` (эрх огт байхгүй) нь энд ОРОХГҮЙ: `Math.max()` нь
       хоосон массивт `-Infinity` буцаадаг тул `length` шалгахгүй бол
       эрхгүй хүн ч «1-2 шат» болж, нэгжийн сонгогч чимээгүй
       нуугдана. */
  const stage12 = React.useMemo(() => {
    const st = allowedStages(user, isAdmin);
    return st.length > 0 && Math.max(...st) <= 2;
  }, [user, isAdmin]);

  /* ── МОНИТОРИНГ ХИЙДЭГ НЭГЖИЙН ЛАВЛАХ (2026-10-28) ────────────────
     Хэрэглэгч: «мониторингоор явдаг БН-н бүртгэл … энэ БН-д л энэ
     хяналт хийх товч харагддаг болгох».

     ⚠ СОНГОСОН нэгжээр шийднэ, нэвтэрсэн хүний нэгжээр БИШ: ХАБЭА
       хэлтсийн мэргэжилтэн бусад цехэд ч шалгалт эхлүүлдэг тул
       «энэ БН мониторинг хийдэг эсэх» нь ТУХАЙН шалгалтын нэгжээс
       шалтгаална. Иймд серверийн `covered` тугийг (тэр нь зөвхөн
       НЭВТЭРСЭН хүний нэгжийнх) хэрэглэхгүй, `items`-ээс
       `coversUnit()`-ээр бодно.

     ⚠ Лавлах 7 мөр — нэг л удаа татна (цонх нээгдэх үед).
     ⚠ Унавал/хоосон бол ХААЛТЫГ ХЭРЭГЛЭХГҮЙ (`null` төлөв) — эс
       бөгөөс лавлах татагдаагүйгээс болж мониторинг хаагдаж, бүртгэл
       огт үүсэхгүй болно (2026-09-23-ны «мониторинг унасан» тохиолдол
       дахин давтагдана). */
  const [monRows, setMonRows] = useState(null);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    xabBnMonitoringApi
      .list({})
      .then((d) => {
        if (alive) setMonRows(d?.items || []);
      })
      .catch(() => {
        if (alive) setMonRows(null);
      });
    return () => {
      alive = false;
    };
  }, [open]);
  // Эрх огт тодорхойлогдоогүй (SSO эрх ч, бүлэг ч алга) хэрэглэгчийг
  // бүрэн хаахгүй — хамгийн бага эрхээр I шат нээнэ. Жинхэнэ хаалт
  // backend дээр (Resources/XAB/inspection.py) хэвээр.
  const codes = myCodes.length ? myCodes : ["I"];
  // Анхдагч шат — эрхийнх нь ЭХНИЙХ (ХАБЭА-д "IV", БН-д "I")
  const firstCode = fixedStage || defaultStage || codes[0] || "I";

  const [bn, setBn] = useState({
    code: bnCode || "",
    name: bnName || "",
    parentId: null,
  });
  const [section, setSection] = useState({
    code: tsehCode || "",
    name: tsehName || "",
  });
  /* ГРУПП — хэсгээс нэг түвшин доош (2026-10-23, хэрэглэгч:
     «хяналт шалгалтыг хэсэг дотроо группээр хийдэг»).
     ⚠ ЗААВАЛ БИШ: 208 хэсгийн зөвхөн 24-д групп байдаг тул шаардлагатай
       болговол бусад 184 хэсэгт шалгалт эхлэхгүй болно. */
  const [grp, setGrp] = useState({ code: "", name: "" });
  // Эцгээс ирсэн нэгж — сонгогч нуугдана
  const deptFixed = !!bnCode;
  const [stageCode, setStageCode] = useState(firstCode);

  // Үүссэн шалгалтыг бөглөх модал
  const [fillId, setFillId] = useState(null);
  /* ТОХИРГООНЫ ХЭСЭГ дэлгэгдсэн эсэх (шат · нэгж · хэсэг · шалгагч).
   *
   * ⚠ ЯАГААД ХУРААДАГ БОЛОВ (2026-09-30, хэрэглэгч): шалгалт эхэлмэгц
   *   шалгах хуудас нь тохиргооны маягтын ДООР гарч ирдэг тул үзүүлэлт
   *   бөглөх бүрд доош гүйлгэх шаардлагатай байв. Шалгалт үүссэн хойно
   *   тохиргоо нь дахин хэрэггүй (модалын толгойд шат/нэгж харагдаж
   *   байгаа) — тиймээс автоматаар хураана, хэрэгтэй бол дэлгэнэ. */
  const [setupOpen, setSetupOpen] = useState(true);

  // Тухайн нэгжийн батлагдсан хуудас — байгаа эсэхийг урьдчилан мэдэгдэнэ
  const [sheet, setSheet] = useState(null);

  /* ── ШАЛГАСАН АЖЛЫН БАЙР — ЛАВЛАХААС ──────────────────────────────────
     Өмнө нь чөлөөт бичвэр байсан тул нэг ажлын байрыг «Насос станц»,
     «насос-1», «Насосны өрөө» гэх мэт өөр өөрөөр бичиж, «аль байрыг
     хэдэн удаа шалгасан» гэдэг дүн шинжилгээ хийх боломжгүй байв.
     Одоо БН-ийн ажлын байрны лавлахаас (xab.bn_workplace) татаж сонгуулна.

     ⚠ Чөлөөт бичвэрийг ХААГААГҮЙ: лавлахад одоогоор ердөө 1 бүртгэл
     байгаа тул хатуу шаардвал шалгалт огт эхлэхгүй болно. AutoComplete
     нь санал болгоно, гэхдээ шинэ нэр бичихийг зөвшөөрнө. */
  const [workplaces, setWorkplaces] = useState([]);
  const [workplaceId, setWorkplaceId] = useState(null);
  /* ── ШАЛГАСАН АЖЛЫН БАЙРУУДЫН ЧЕКЛИСТ (2026-10-29) ──────────────
     Хэрэглэгч: «Шалгасан ажлын байруудыг чек листээр харуулж зэрэг
     хяналтын хуудас эхлүүлж, хэрвээ зөрчилгүй бол бүгд бүртгэгдэж».

     Сонгосон хувилбар (хэрэглэгчийн шийдвэр): үзсэн байр тутамд
     ТУСДАА шалгалт үүснэ. Тиймээс шинэ хүснэгт/DDL ХЭРЭГГҮЙ —
     `xab.inspection` мөр бүр нь өөрийн `workplace_id`-тай болно.

     ⚠ Шалгалтын ТОО олшрохыг хэрэглэгч МЭДСЭЭР сонгов: 5 байр үзвэл
       5 INS- дугаар. Тайлан/төлөвлөгөөний гүйцэтгэл тэр тооллогоор
       уншигдана. */
  const [wpChecked, setWpChecked] = useState([]);
  /* ── БАЙРШИЛ (GPS) ───────────────────────────────────────────────
     Шалгалт ХААНА хийгдсэнийг газар зүйн байршлаар тогтооно.
     `area_location` нь ажлын байрны НЭР (лавлахаас) — өөр зүйл. */
  const [gps, setGps] = useState(null);
  const [gpsState, setGpsState] = useState("idle");
  /* Алдааны ШАЛТГААН — заавар нь түүнээс хамаарна.
     "denied" · "unavailable" · "timeout" · "insecure" · "unsupported" */
  const [gpsErr, setGpsErr] = useState(null);

  /* ⚠ ГАРААР ОРУУЛАХ нь ТОХИРГООНЫ АСУУДАЛ БИШ, БОДИТ ХЭРЭГЦЭЭ:
     компьютерт GPS төхөөрөмж байдаггүй тул хөтөч WiFi/IP-ээр тооцдог
     ба дотоод сүлжээнд тэр ажиллахгүй байж болно. Тийм үед ажилтан
     Google Maps-аас координатаа хуулж тавина. */
  const [manual, setManual] = useState(false);
  const [manualText, setManualText] = useState("");

  const applyManual = () => {
    /* «47.92123, 106.91234» · «47.92123,106.91234» · хооронд нь
       цэг таслалгүй ч байж болно — тоонуудыг нь салгаж авна. */
    const raw = String(manualText || "").trim();
    /* ⚠ ГРАДУСЫН ХЭЛБЭРИЙГ ТАТГАЛЗАНА (47°55'12.4"N). Тоонуудыг нь
       салгавал 47 ба 55 гэж уншигдаж, МИНУТЫГ уртраг болгон ЧИМЭЭГҮЙ
       буруу цэг бүртгэнэ. Хөрвүүлэхийг оролдохоос татгалзах нь аюулгүй. */
    if (/[°'"′″NSEW]/i.test(raw)) {
      message.warning(
        "Аравтын бутархайгаар оруулна уу (жишээ: 47.92123, 106.91234)",
      );
      return;
    }
    /* ⚠ ЯГ ХОЁР тоо шаардана. Өмнө нь `split` ашигласан тул урд талын
       зай «» гэсэн хоосон хэсэг үүсгэж `Number("")=0` болон, «abc» нь
       0,0 (Гвинейн булан) болж ЧИМЭЭГҮЙ хүлээн авагдаж байв. */
    const nums = (raw.match(/-?\d+(?:\.\d+)?/g) || []).map(Number);
    if (nums.length !== 2 || !nums.every(Number.isFinite)) {
      message.warning("Яг хоёр тоо: 47.92123, 106.91234");
      return;
    }
    const [lat, lng] = nums;
    /* ⚠ ХҮРЭЭГ ШАЛГАНА: сольж бичих нь түгээмэл алдаа (Google Maps-аас
       хуулахад эхэнд өргөрөг). Хүрээнээс гарвал цэг огт өөр тивд
       буух тул чимээгүй хүлээж авч болохгүй. */
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      message.error("Өргөрөг ±90, уртраг ±180 хооронд байна");
      return;
    }
    setGps({ lat, lng, accuracy: null });
    setGpsState("ok");
    setManual(false);
    setManualText("");
  };

  const grabGps = () => {
    if (!navigator.geolocation || window.isSecureContext === false) {
      /* ⚠ «Төхөөрөмж дэмжихгүй» гэж ХЭЛЭХГҮЙ — ихэнхдээ төхөөрөмж биш,
         ХАЯГ нь буруу (HTTPS биш). Хөтөч үүнийг чимээгүй хориглодог. */
      setGpsErr(
        window.isSecureContext === false ? "insecure" : "unsupported",
      );
      message.warning("Байршил авах боломжгүй — доорх зааврыг уншина уу");
      setManual(true);
      return;
    }
    setGpsState("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setGps({ lat: latitude, lng: longitude, accuracy });
        setGpsState("ok");
        setGpsErr(null);
      },
      (err) => {
        setGpsState("error");
        /* ⚠ Шалтгааныг ЯЛГАЖ хадгална: «зөвшөөрөл өгөөгүй» (хөтчөөс
           сэргээнэ) ба «GPS төхөөрөмжгүй» (сэргээх юм алга) хоёрын
           шийдэл ТЭС ӨӨР. Дэлгэрэнгүй заавар нь доорх Alert-д —
           toast нь 3 секундэд алга болдог тул олон алхамт зааварт
           таарахгүй. */
        setGpsErr(
          err?.code === 1
            ? "denied"
            : err?.code === 3
              ? "timeout"
              : "unavailable",
        );
        message.warning("Байршил авч чадсангүй — доорх зааврыг уншина уу");
        /* Бүтэлгүйтэхэд гар оролтыг НЭЭНЭ: дахин дахин товч дарах нь
           ижил үр дүн өгнө. */
        setManual(true);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };
  /* Хяналт хийх ажилтан (хариуцагч). Хоосон бол НЭВТЭРСЭН хэрэглэгч —
     backend `inspector_tn or usertn` гэж боддог тул зан төлөв
     өөрчлөгдөхгүй, зөвхөн ӨӨР хүнийг заах боломж нэмэгдэнэ.
     Журам 2.8.3: байнгын хяналтыг ХАБЭА-н инженер явуулна — бүртгэгч
     нь өөр хүн байж болно. */
  const [inspector, setInspector] = useState(null);
  /* Ажилтны тоо — сонгосон БН/хэсгийн ажилтны тоогоор УРЬДЧИЛАН бөглөнө.
     ⚠ `countAuto` нь ГАРААР засагдсан утгыг хамгаална: нэгж солигдох бүрд
       дарж бичвэл хэрэглэгчийн оруулсан бодит тоо алга болно. */
  const [countAuto, setCountAuto] = useState(true);
  const [countLoading, setCountLoading] = useState(false);
  const [scopeCount, setScopeCount] = useState(null);

  /* ХАРИУЦАГЧ — цонх нээгдэхэд НЭВТЭРСЭН хэрэглэгчээр бөглөнө.

     ⚠ Яагаад `initialWorker`-т ганц TN биш НЭРИЙГ нь ч өгч байна вэ:
       WorkerSelect нь сонгогдсон хүнийг хайлтын үр дүнгээс ХАЙДАГ, олдохгүй
       бол зөвхөн дугаар харагдана. Нэвтэрсэн хүн өөрийн цехийн жагсаалтад
       байхгүй ч байж болзошгүй (ХАБЭА хэлтсийнх өөр цехийг шалгах гэх мэт)
       тул нэрийг нь шууд дамжуулна.

     ⚠ Цонх ХААГДАХАД дахин тавина — өмнөх шалгалтад өөр хүн сонгосон бол
       дараагийнх нь тэр хүнээр нээгдэх ёсгүй. */
  useEffect(() => {
    if (!open) return;
    const tn = String(user?.tn || user?.info?.tn || "");
    if (!tn || tn === "0") return;
    setInspector({
      tn,
      name: user?.info?.shortname || user?.info?.name || "",
      position: user?.info?.position_namemn || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* Сонгосон БН/хэсгийн АЖИЛТНЫ ТОО — лавлахаас (Redis кэш).
     `/xab/workers` нь `total`-ыг limit-ээс ӨМНӨ тооцож буцаадаг тул
     1 мөр л татаад тоог уншина (7000 мөр татах шаардлагагүй). */
  useEffect(() => {
    if (!open || !bn.code) {
      setScopeCount(null);
      return;
    }
    let alive = true;
    setCountLoading(true);
    xabInspectionApi
      .searchWorkers({
        bn_code: bn.code,
        tseh_code: section.code || undefined,
        // ⚠ ГРУПП сонгогдсон бол түүгээр хумина — ID-гаар (код биш).
        //   Хэмжсэн: хэсэг 2505 = 21 ажилтан, үүнээс групп 250501
        //   (id 2315) = 17. Өмнө нь групп сонгосон ч 21 гарч байв.
        grp_id: grp.id || undefined,
        limit: 1,
      })
      .then((d) => {
        if (!alive) return;
        const n = Number(d?.total ?? 0);
        setScopeCount(n);
        // ⚠ Гараар засаагүй үед л бөглөнө
        if (countAuto) form.setFieldsValue({ worker_count: n });
      })
      .catch(() => alive && setScopeCount(null))
      .finally(() => alive && setCountLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bn.code, section.code, grp.id]);

  useEffect(() => {
    if (!open) {
      setWorkplaces([]);
      return;
    }
    /* ⚠ `bn_code`-ыг СЕРВЕР РҮҮ ЯВУУЛАХГҮЙ.
       Backend нь `bn_code == '25'` гэж ЯГ ТЭНЦҮҮГЭЭР шүүдэг. Гэтэл лавлах
       дээрх бичлэгийн `bn_code` нь ҮҮСГЭСЭН ХҮНИЙ нэгж болж бичигдсэн
       байдаг (жишээ нь 0107 = хэлтэс) атал `tseh_code` нь огт өөр модны
       хэсэг (2505) байж болно. Тийм бичлэг ЯМАР Ч цехээр хайхад олдохгүй.

       Тиймээс серверээс ЭРХИЙН ХҮРЭЭНИЙ бүх мөрийг аваад, доор кодын
       УГТВАРААР (цех 2 орон · хэлтэс 4 орон · хэсэг угтвартай) тулгана. */
    xabBnWorkplaceApi
      .list({})
      .then((d) => setWorkplaces(d?.items || []))
      .catch(() => setWorkplaces([]));
  }, [open]);

  /* Сонгосон цех/хэсэгт хамаарах байруудыг УГТВАРААР ялгана.
     `bn_code` эсвэл `tseh_code`-ийн АЛЬ НЭГ нь таарвал хамаарна гэж үзнэ —
     лавлах дээрх кодын зөрүүг ингэж тэвчинэ. */
  /* ⚠ ШҮҮСЭН МӨРҮҮД нь ТУСДАА memo (2026-10-29): «Шалгасан ажлын
     байр» сонгогч ба ЧЕКЛИСТ хоёулаа ЯГ ижил хумилттай байх ёстой.
     Шүүлтийг хоёр дахин бичвэл хожим зөрнө.

     ━━ ХАТУУ ХУМИЛТ (2026-10-29, хэрэглэгчийн гомдол) ━━━━━━━━━━━━━━
     «Шалгасан ажлын байрууд (чеклист) дээр хамаагүй ажлын байрууд орж
      ирж байна. ажлын байрыг хэсэг нэгж групптэй холбосон доо».

     ⚠ ӨМНӨХ ГЭМ: `want = [section.code, bn.code]` дээр `.some()`
       хийдэг байсан бөгөөд `near(w.tseh_code, bn.code)` нь тухайн
       ЦЕХИЙН БҮХ мөрт тэнцдэг. Улмаас хэсэг сонгосон ч шүүлт
       УТГАГҮЙ болж, бүх хэсгийн байр орж ирдэг байв.
       Хэмжсэн: цех 23 · хэсэг 2335 → 106 мөр (ҮНЭН нь 16);
                цех 25 · хэсэг 2505 → 16 мөр (ҮНЭН нь 1).

     ⚠ ЦЕХ нь ҮРГЭЛЖ таарах ёстой. `bn_code` хоосон мөр нь «ТЭР
       ЦЕХИЙН бүх хэсэгт» гэсэн утгатай — БҮХ цехийнх БИШ. Цехээр
       хумихгүй бол цех 23-ын шалгалтад цех 25-ын лабораторийн
       өрөөнүүд орж ирдэг (симуляцаар баталсан).

     ⚠ `tseh_code` ХООСОН мөрийг `bn_code`-оор авна: лавлахын хуучин
       бичлэгүүдэд цехийн код дутуу байдаг (файлын 530-р мөрийн
       тайлбарыг үз). */
  /* ⚠ ЛОГИК нь `workplaceScope.js`-д, ТЕСТТЭЙ (14 тест). Энэ шүүлт
     ХОЁР удаа гэм гаргасан (группийн хумилт алга; цехийн кодоор
     тэнцэж хэсгийн шүүлт утгагүй болсон) тул компонентод эргүүлж
     бичихгүй — модулийг л дуудна. */
  const wpFiltered = React.useMemo(
    () =>
      filterWorkplaces(workplaces, {
        tseh: bn.code,
        section: section.code,
        grp: grp.code,
      }),
    [workplaces, section.code, bn.code, grp.code],
  );

  const workplaceRows = wpFiltered.rows;

  const workplaceOptions = React.useMemo(
    () =>
      workplaceRows.map((w) => ({
        value: w.name,
        id: w.id,
        label: (
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: 600 }}>
              {w.name}
              {w.is_high_risk ? (
                <span style={{ color: "#DC2626", marginLeft: 6, fontSize: 11 }}>
                  өндөр эрсдэлт
                </span>
              ) : null}
            </div>
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>
              {[w.code, w.bn_name].filter(Boolean).join(" · ") ||
                "цехийн бүх хэсэгт"}
            </div>
          </div>
        ),
      })),
    [workplaceRows],
  );

  useEffect(() => {
    xabInspectionApi
      .getStages()
      .then((d) => setStages(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (fixedStage) setStageCode(fixedStage);
  }, [fixedStage]);

  useEffect(() => {
    if (bnCode) setBn({ code: bnCode, name: bnName || "", parentId: null });
    if (bnCode) setSection({ code: tsehCode || "", name: tsehName || "" });
    /* ⚠ Нэгж солигдоход чеклистийг ЦЭВЭРЛЭНЭ: өмнөх нэгжийн ажлын
       байр сонгогдсон хэвээр үлдвэл тэр нэгжид шалгалт үүсгэнэ. */
    setWpChecked([]);
  }, [bnCode, bnName, tsehCode, tsehName]);

  useEffect(() => {
    if (!open || !stageCode || !bn.code || String(bn.code) === "0") {
      setSheet(null);
      return;
    }
    xabChecklistSheetApi
      .list({ stage_code: stageCode, tseh_code: bn.code })
      .then((d) => {
        const rows = d?.sheets || [];
        setSheet(rows.find((x) => x.is_effective) || null);
      })
      .catch(() => setSheet(null));
  }, [open, stageCode, bn.code]);

  // ── Тухайн шатны ШАЛГАХ ХУУДСУУД ──────────────────────────────────────
  // I/II/III шатанд нэг хуудас (автоматаар олдоно), IV шатанд мэргэжилтэн
  // тус бүрийн 20 хуудас байдаг тул АЛЬ хуудсаар шалгахаа сонгоно.
  const [templates, setTemplates] = useState([]);
  // Гүйцэтгэгчийн лавлахаар МИНИЙ хариуцах хуудсуудын id
  const [myIds, setMyIds] = useState([]);
  useEffect(() => {
    if (!open || !stageCode) return;
    // Бүх хуудас + надад хамаарах нь — хоёуланг татаж, сонголтыг бүлэглэнэ.
    // Ингэснээр хариуцсан хуудсаа шууд олох ба шаардлагатай үед бусдыг ч
    // сонгох боломжтой (жишээ нь орлон ажиллах үед).
    Promise.all([
      /* ⚠ `bn_code` — БН-ийн ӨӨРИЙН батлагдсан хуудсыг нийлүүлэхэд
         ЗААВАЛ (2026-10-29). Сервер нь журмын хуудсыг үргэлж, БН-ийнхийг
         зөвхөн тухайн хэсэгт л гаргана; `bn_code` явуулахгүй бол БН-ийн
         хуудас ОГТ харагдахгүй. */
      xabInspectionApi.getTemplate({
        list: 1,
        stage_code: stageCode,
        bn_code: section.code || bn.code || undefined,
      }),
      xabInspectionApi
        .getTemplate({ list: 1, stage_code: stageCode, mine: 1 })
        .catch(() => []),
    ])
      .then(([all, mine]) => {
        const rows = Array.isArray(all) ? all : [];
        const mineRows = Array.isArray(mine) ? mine : [];
        setTemplates(rows);
        setMyIds(mineRows.map((t) => t.id));
        // Нэг хуудастай шат — автоматаар. Олон хуудастай үед надад
        // ганцхан хамаарч байвал түүнийг сонгоно.
        const pick =
          rows.length === 1
            ? rows[0].id
            : mineRows.length === 1
              ? mineRows[0].id
              : undefined;
        form.setFieldsValue({ template_id: pick });
      })
      .catch(() => {
        setTemplates([]);
        setMyIds([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stageCode]);

  // ── ЗОРИЛТОТ / ЭЭЛЖИТ БУС — УДИРДАМЖААР ───────────────────────────────
  // Журам эдгээрт тогтсон маягт заагаагүй (зорилтот — «Удирдамжаар»,
  // ээлжит бус — «—») тул шалгах асуудал нь удирдамжаас ирнэ.
  // Лавлах: /incident/reference → «Хяналтын удирдамж»
  /* ── ЦОНХНЫ ГАРЧИГ — ХУВЬСАНА ────────────────────────────────────────
     «Хяналт шалгалт эхлүүлэх» гэсэн тогтмол гарчиг нь ЮУГ, ХААНА шалгаж
     байгааг хэлдэггүй: шалгах хуудас нээгдсэний дараа ч цонх нь ижил
     харагдана. Утсан дээр бол доорх сонголтууд гүйлгэхэд халхлагдаж,
     хэрэглэгч аль нэгж дээрээ байгаагаа мартдаг.

     Бүтэц:  <шатны нэр> · <цех> · <хэсэг>   (+ үүссэн шалгалтын код)
     Сонголт хийгдээгүй хэсгүүд чимээгүй унана. */
  const stageLabel = React.useMemo(() => {
    const st = stages.find((x) => x.code === stageCode);
    return st ? st.short_name || st.name : "";
  }, [stages, stageCode]);

  const modalTitle = React.useMemo(() => {
    const unit = [bn.name || bn.code, section.name || section.code]
      .filter(Boolean)
      .map((x) => String(x).trim())
      .filter(Boolean)
      .join(" · ");
    const head = stageLabel || "Хяналт шалгалт";
    // Шалгалт үүссэн (fillId) → «эхлүүлэх» биш, бөглөж байна
    const verb = fillId ? "" : " эхлүүлэх";
    return { head: head + verb, unit };
  }, [stageLabel, bn.name, bn.code, section.name, section.code, fillId]);

  /* Шалгалт үүсмэгц (fillId) тохиргоог хураана. Цонх хаагдахад буцаад
     дэлгэнэ — дараагийн шалгалт эхлүүлэхэд тохиргоо нь харагдах ёстой. */
  useEffect(() => {
    setSetupOpen(!fillId);
  }, [fillId]);

  /* ⚠ ШАТ СОЛИГДОХОД чеклэсэн байруудыг ЦЭВЭРЛЭНЭ: I шатанд 11 байр
     тэмдэглээд III болгоход тэр сонголт үлдвэл III шатанд 11 шалгалт
     үүсгэх гэж оролдоод сервер 400 буцаана (хэрэглэгч шалтгааныг
     ойлгохгүй). */
  useEffect(() => {
    if (["III", "IV"].includes(stageCode)) setWpChecked([]);
  }, [stageCode]);

  const needsDirective = DIRECTIVE_STAGE_CODES.includes(stageCode);
  const [directives, setDirectives] = useState([]);
  useEffect(() => {
    if (!open || !needsDirective) {
      setDirectives([]);
      return;
    }
    xabInspectionDirectiveApi
      .list({ stage_code: stageCode, tseh_code: bn.code || undefined })
      .then((d) => {
        const rows = (d?.items || []).filter(
          (x) =>
            // Мөргүй удирдамжаар шалгалт эхлүүлэх боломжгүй
            (x.item_count || 0) > 0 &&
            // ЭРХИЙН ТҮВШИН: БН-ийн ажилтан үйлдвэрийн даргын удирдамжаар
            // шалгалт эхлүүлэхгүй — тэр нь ХАБЭА хэлтсийн ажил.
            myLevels.includes(x.level_code || "factory"),
        );
        setDirectives(rows);
        // Ноорог удирдамжийг АВТОМАТААР сонгохгүй (доор disabled болно)
        const usable = rows.filter((x) => x.status !== "draft");
        form.setFieldsValue({
          directive_id: usable.length === 1 ? usable[0].id : undefined,
        });
      })
      .catch(() => setDirectives([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stageCode, bn.code, needsDirective, myLevels]);

  /* `force` — давхардлын анхааруулгыг ИЛ зөвшөөрч дарж өнгөрөх.
     Журмын 2.5.2-оор тархсан объекттой 4 нэгжид 2 комисс ЗЭРЭГ явж
     болдог тул энэ гарц ЗААВАЛ байх ёстой. */
  const doCreate = async (force) => {
    try {
      const v = await form.validateFields();
      if (!bn.code || String(bn.code) === "0") {
        message.warning("Бүтцийн нэгжээ сонгоно уу");
        return;
      }
      setCreating(true);
      // IV шатанд олон хуудас — аль нэгийг заавал сонгоно
      if (!needsDirective && templates.length > 1 && !v.template_id) {
        message.warning("Шалгах хуудсаа сонгоно уу");
        return;
      }
      // Зорилтот / ээлжит бус — удирдамжгүйгээр эхлүүлэх боломжгүй
      if (needsDirective && !v.directive_id) {
        message.warning("Удирдамжаа сонгоно уу");
        return;
      }
      /* ── ЧЕКЛИСТЭЭР ОЛОН ШАЛГАЛТ (2026-10-29) ──────────────────
         Хэрэглэгчийн шийдвэр: «Үзсэн байр тутамд ТУСДАА шалгалт
         үүсгэх». Байр тус бүрд `xab.inspection` мөр үүснэ — шинэ
         хүснэгт/DDL хэрэггүй.

         ⚠⚠ `force=1`-ийг ХАСАВ (2026-11-04). Өмнө нь ХОЁРДУГААРААС
           хойших мөрд `force: i > 0` дамжуулдаг байв: тэр үед серверийн
           давхардлын хориг ажлын байрыг ТООЦДОГГҮЙ тул нэг нэгжийн 5
           байрт 5 шалгалт үүсгэхэд 2-5 дахь нь 409 унадаг байсан.
           Одоо I·II шатанд хориг нь (нэгж + АЖЛЫН БАЙР) хосоор
           тулгадаг болсон (хэрэглэгч 2026-11-04: «1-2 шатны хяналт
           өдөрт БН хэсэг групп эсвэл ажлын байраар ялгаатай үүсэж
           болно») — өөр байрууд ҮҮСНЭ, харин ЯГ ТЭР байранд өнөөдөр
           хуудас байвал давхардал гэж зөв хэлнэ. `force`-ыг үлдээвэл
           тэр хориг чеклистээр ҮРГЭЛЖ дайжиж, дахин хоосон хуудас
           үржинэ.
         ⚠ ЧЕКЛИСТ нь ЗӨВХӨН I·II шатанд харагдана (доорх 1550-р мөр,
           III·IV-д `setWpChecked([])`) тул III шатны САРЫН хоригт энэ
           урсгал хүрэхгүй.
         ⚠ ДАРААЛЖ үүсгэнэ, зэрэг БИШ: дугаар (`code`) нь серверт
           дараалал сайжруулдаг тул зэрэг явуулбал давхардал гарч
           болзошгүй. 5-10 мөр тул хугацаа нь мэдэгдэхүйц биш. */
      if (wpChecked.length > 0) {
        const picked = workplaceRows.filter((w) =>
          wpChecked.includes(w.id),
        );
        const okList = [];
        const dupList = [];
        const failList = [];
        for (let i = 0; i < picked.length; i += 1) {
          const w = picked[i];
          try {
            const one = await xabInspectionApi.create({
              force: force ? 1 : undefined,
              stage_code: v.stage_code,
              template_id: v.template_id || undefined,
              directive_id: v.directive_id || undefined,
              /* Байрны НЭР нь шалгалтын `area_location` болно — хуучин
                 тайлан, хэвлэх маягт тэр талбараас уншдаг. */
              area_location: w.name,
              workplace_id: w.id,
              gps: gps || undefined,
              tseh_code: bn.code || undefined,
              tseh_name: bn.name || undefined,
              bn_code: section.code || undefined,
              bn_name: section.name || undefined,
              grp_code: grp.code || undefined,
              grp_name: grp.name || undefined,
              worker_count: v.worker_count,
              inspector_tn: inspector?.tn || undefined,
              inspection_date: v.inspection_date
                ? v.inspection_date.format("YYYY-MM-DD HH:mm:ss")
                : undefined,
            });
            okList.push({ id: one.id, code: one.code, name: w.name });
          } catch (oneErr) {
            const od = oneErr?.response?.data;
            if (oneErr?.response?.status === 409 && od?.duplicate) {
              dupList.push(w.name);
            } else {
              failList.push(w.name);
            }
          }
        }
        /* `setCreating(false)` хэрэггүй — доорх `finally` нь `return`
           дээр Ч ажиллана. */
        if (!okList.length) {
          message.error(
            `Шалгалт үүсгэж чадсангүй (${
              dupList.length ? "давхардал" : "алдаа"
            }: ${[...dupList, ...failList].join(", ")})`,
          );
          return;
        }
        message.success(
          `${okList.length} ажлын байранд шалгалт үүслээ` +
            (dupList.length ? ` · ${dupList.length} давхардсан` : "") +
            (failList.length ? ` · ${failList.length} унасан` : ""),
        );
        /* ⚠ ЭХНИЙХИЙГ нээж бөглүүлнэ. Бусад нь жагсаалтад «эхэлсэн»
           төлөвтэй үлдэнэ — хэрэглэгч дараалан орж бөглөнө. Бүгдийг
           зэрэг нэг цонхонд нээх нь ХАМААРАЛТАЙ ажил (шалгах хуудас
           бүр өөрийн үзүүлэлттэй) тул энд хийгээгүй. */
        setWpChecked([]);
        setFillId(okList[0].id);
        return;
      }

      const res = await xabInspectionApi.create({
        force: force ? 1 : undefined,
        stage_code: v.stage_code,
        template_id: v.template_id || undefined,
        directive_id: v.directive_id || undefined,
        area_location: v.area_location,
        /* Сервер `gps:{...}` ба хавтгай `gps_lat` хоёуланг хүлээн авна;
           энд объектоор явуулав (аюулын модультай ижил). */
        gps: gps || undefined,
        // Лавлахаас сонгосон бол ажлын байрны id — дүн шинжилгээнд
        workplace_id: workplaceId || undefined,
        // xab.inspection: tseh_* = ЦЕХ/хэлтэс, bn_* = ХЭСЭГ (2026-09-17-ноос)
        tseh_code: bn.code || undefined,
        tseh_name: bn.name || undefined,
        bn_code: section.code || undefined,
        bn_name: section.name || undefined,
        // Групп — хэсгийн доторх ажлын групп (заавал биш)
        grp_code: grp.code || undefined,
        grp_name: grp.name || undefined,
        worker_count: v.worker_count,
        inspector_tn: inspector?.tn || undefined,
        inspection_date: v.inspection_date
          ? v.inspection_date.format("YYYY-MM-DD HH:mm:ss")
          : undefined,
      });
      message.success(`Шалгалт үүслээ: ${res.code}`);
      // ⚠ Цонхыг ХААХГҮЙ, маягтыг ч цэвэрлэхгүй: шалгах хуудас нь ЭНЭ
      // цонхны дотор доор нь гарч ирнэ. Хэрэглэгч сонгосон шат/нэгж/хуудсаа
      // харсаар үзүүлэлтүүдийг бөглөнө — нэг урсгал.
      setFillId(res.id);
    } catch (e) {
      if (e?.errorFields) return;
      const d = e?.response?.data;
      /* ⚠ ДАВХАРДСАН III ШАТ (409). Зүгээр алдаа харуулбал хэрэглэгч
         ГАЦНА — тиймээс гарц өгнө.

         ⚠ ДАВХАРДАЛ ҮҮСГЭХ нь ЗӨВХӨН САНААТАЙ үйлдэл байна:
           · OK (өргөтгөсөн) = байгаа шалгалт руу орох — журмын ЗӨВ зам;
           · Болих ба × — ЮУ Ч ХИЙХГҮЙ;
           · шинээр үүсгэх нь ДОТОРХ тусдаа товч.
         Анх `onCancel`-д тавихаар бодсон боловч antd нь × ба mask-аар
         хаахад Ч `onCancel`-ыг дууддаг тул цонхыг хаахад САНАМСАГҮЙ
         давхардал үүсэх байсан. */
      if (e?.response?.status === 409 && d?.duplicate && d?.id) {
        let inst;
        inst = Modal.confirm({
          title: "Энэ нэгжид эхэлсэн шалгалт байна",
          width: 560,
          content: (
            <div style={{ lineHeight: 1.5 }}>
              <div style={{ marginBottom: 8 }}>{d.message}</div>
              <div style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>
                {d.code}
                {/* ⚠ АЖЛЫН БАЙРЫГ ХАРУУЛНА (2026-11-04): I·II шатанд
                    давхардал нь ЯГ ТЭР байранд л тооцогддог болсон тул
                    хэрэглэгч аль байр мөргөлдсөнийг харах ёстой. */}
                {d.workplace_name ? ` · ${d.workplace_name}` : ""}
                {d.inspector_name ? ` · ${d.inspector_name}` : ""}
                {d.inspection_date
                  ? ` · ${String(d.inspection_date).slice(0, 10)}`
                  : ""}
              </div>
              <Button
                size="small"
                danger
                onClick={() => {
                  if (inst) inst.destroy();
                  doCreate(true);
                }}
              >
                {/* Журмын 2.5.2 нь III шатны КОМИССЫН тухай — I·II шатны
                    өдрийн давхардалд тэр зүйлийг дурдвал төөрөгдөнө. */}
                {stageCode === "III"
                  ? "Журмын 2.5.2 — зэрэг 2 комисс, шинээр үүсгэх"
                  : "Ийм ч байсан шинээр үүсгэх"}
              </Button>
            </div>
          ),
          okText: "Байгаа шалгалт руу орох",
          cancelText: "Болих",
          onOk: () => setFillId(d.id),
        });
        return;
      }
      message.error(d?.message || "Шалгалт үүсгэхэд алдаа гарлаа");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} style={buttonStyle}>
        <i className="fas fa-clipboard-check" style={{ marginRight: 8 }} />
        {buttonLabel || autoLabel(codes)}
      </button>

      {/* ── 1. Шалгалт эхлүүлэх ── */}
      <Modal
        title={
          <div style={{ lineHeight: 1.25, paddingRight: 24 }}>
            <div style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15 }}>
              {modalTitle.head}
            </div>
            {modalTitle.unit ? (
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: "#8A94A6",
                  marginTop: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <i className="fas fa-sitemap" style={{ marginRight: 5 }} />
                {modalTitle.unit}
              </div>
            ) : null}
          </div>
        }
        open={open}
        destroyOnClose
        onCancel={() => {
          setOpen(false);
          // Дараагийн удаа шинээр эхлэхийн тулд төлөвөө цэвэрлэнэ
          if (fillId) {
            setFillId(null);
            form.resetFields();
            onFinished?.();
          }
        }}
        /* ⚠ `onOk={doCreate}` гэвэл antd нь event-ийг аргумент болгон
           дамжуулж `force` нь truthy болно — давхардлын анхааруулга
           ЧИМЭЭГҮЙ алгасагдана. Тиймээс заавал боож дуудна. */
        onOk={() => doCreate(false)}
        confirmLoading={creating}
        okText="Эхлүүлэх"
        cancelText="Болих"
        // Шалгалт үүссэний дараа доор нь шалгах хуудас гарах тул цонх өргөснө.
        // Утсан дээр — БҮТЭН ДЭЛГЭЦ (зай алдахгүй, хажуу тийш гүйлгэхгүй).
        /* ⚠ ЭХНЭЭСЭЭ ӨРГӨН. Урьд нь 620 → шалгах хуудас нээгдэхэд 1040
           болж ҮСРЭХ тул сонголтууд байрлалаа солиод хэрэглэгч алдаг байв.
           Мөн 620-д «Шалгах хуудас», «Ажлын байр», хуудасны мэдээлэл (SheetInfo)
           шахагдаж 2-3 мөр болж давхарладаг. Утсан дээр бүтэн дэлгэц. */
        width={isMobile ? "100vw" : 1040}
        style={
          isMobile
            ? { top: 0, margin: 0, maxWidth: "100vw", paddingBottom: 0 }
            : { top: 20 }
        }
        bodyStyle={isMobile ? { padding: "12px 12px 16px" } : undefined}
        // Хуудас нээгдсэний дараа «Эхлүүлэх» товч утгагүй — бөглөх дэлгэц
        // өөрийн хадгалах/хаах товчтой.
        footer={fillId ? null : undefined}
        okButtonProps={isMobile ? { size: "large", block: true } : undefined}
        cancelButtonProps={
          isMobile
            ? { size: "large", block: true, style: { marginBottom: 8 } }
            : undefined
        }
      >
        <div
          style={
            isMobile
              ? { maxHeight: "calc(100vh - 130px)", overflowY: "auto", overflowX: "hidden" }
              : fillId
                ? { maxHeight: "80vh", overflowY: "auto" }
                : undefined
          }
        >
        {/* ── ТОХИРГООНЫ ХУРААНГУЙ — зөвхөн шалгалт үүссэн хойно ──── */}
        {fillId ? (
          <button
            type="button"
            onClick={() => setSetupOpen((v) => !v)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 8,
              textAlign: "left",
              background: "#F6F8FB",
              border: "1px solid #E6E9EF",
              borderRadius: 8,
              padding: "6px 10px",
              marginBottom: 10,
              cursor: "pointer",
              fontSize: 12,
              color: "#5A6478",
            }}
          >
            <i
              className={setupOpen ? "fas fa-chevron-up" : "fas fa-chevron-down"}
              style={{ fontSize: 10 }}
            />
            <span style={{ fontWeight: 700, color: "#1F2430" }}>
              Шалгалтын тохиргоо
            </span>
            {/* Хурааж байхад ЮУ СОНГОСНОО харуулна — эс бөгөөс хэрэглэгч
                «аль нэгжийг шалгаж байна» гэдгээ мартаж, дэлгэж шалгах
                шаардлагатай болно. */}
            {!setupOpen ? (
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {[
                  stageLabel,
                  bn.name || bn.code,
                  section.name || section.code,
                  inspector?.name,
                ]
                  .filter(Boolean)
                  .map((x) => String(x).trim())
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            ) : null}
            <span style={{ marginLeft: "auto", fontWeight: 600 }}>
              {setupOpen ? "Хураах" : "Дэлгэх"}
            </span>
          </button>
        ) : null}

        {/* ⚠ UNMOUNT ХИЙХГҮЙ — `display: none`. Form-ыг салгавал antd-ийн
            талбарын утга (шат · нэгж · хэсэг · шалгагч) арилж, дэлгэхэд
            хоосон маягт гарна. Мөн `SheetInfo`-ийн таталт дахин явна. */}
        <div style={fillId && !setupOpen ? { display: "none" } : undefined}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="stage_code"
            label="Хяналтын шат"
            hidden={!!fixedStage}
            // `firstCode` — fixedStage → defaultStage → эрхийн ЭХНИЙ төрөл.
            // Хоосон үлдээвэл сонгогч нь хоосон харагдаад дотооддоо өөр шатны
            // хуудас татчихдаг зөрүү үүснэ.
            initialValue={firstCode}
            rules={[{ required: true, message: "Шат сонгоно уу" }]}
          >
            <StageButtons
              onChange={setStageCode}
              // Шаталсан үзлэг (I-IV) + зорилтот, ээлжит бус, БАЙНГЫН
              // (мониторинг) хяналт — журмын 1.5.1-1.5.4-ийн бүх төрөл.
              //
              // ⚠ Мониторинг нь өмнө нь «Хавсралт №6 нь ДЭВТЭР» гэсэн
              //   үндэслэлээр энд ороогүй байв. Гэвч журмын 2.8.4 нь нэгж
              //   өөрийн БАЙНГЫН ХЯНАЛТЫН ШАЛГАХ ХУУДСЫГ боловсруулж,
              //   түүгээрээ ээлж тутам хяналт хийхээр заасан — Хавсралт №6
              //   нь үр дүнг бүртгэх дэвтэр (4.3.2), өөр зүйл. Улмаас
              //   мониторингийн шалгалт огт үүсэхгүй, акт ч, сарын тайлан ч
              //   гардаггүй байлаа.
              //
              // ⚠ ЗӨВХӨН хэрэглэгчид ЭРХТЭЙ нь харагдана (`codes`). Өмнө нь
              //   бүгд харагдаж, БН-ийн ажилтан IV шатны хуудсаар шалгалт
              //   эхлүүлж чаддаг байв.
              options={stages
                .filter((s) => codes.includes(s.code))
                /* ⚠ МОНИТОРИНГ — ЗӨВХӨН бүртгэлтэй нэгжид.
                   `monRows === null` (татагдаагүй/унасан) бол ХААХГҮЙ:
                   лавлахын доголдлоос болж журмын ажил зогсохгүй. */
                .filter(
                  (s) =>
                    s.code !== "monitoring" ||
                    monRows === null ||
                    coversUnit(monRows, bn.code, section.code),
                )
                .map((s) => ({
                  value: s.code,
                  label: s.short_name || s.name,
                  hint: s.frequency_label || "",
                  // ⚠ Лавлахаас: өнгө · icon · хэн явуулах
                  color: s.color_hex || "",
                  icon: s.icon || "",
                  actor: s.actor_label || "",
                }))}
            />
          </Form.Item>

          {/* IV шатанд мэргэжилтэн тус бүр өөрийн шалгах хуудастай (20) тул
              аль хуудсаар шалгахаа заавал сонгоно. Нэг хуудастай шатанд
              (I/II/III) энэ сонгогч харагдахгүй. */}
          {/* Зорилтот / ээлжит бус — журам тогтсон маягт заагаагүй тул
              шалгах асуудал нь УДИРДАМЖААС ирнэ. Мөргүй удирдамжийг
              жагсаалтад оруулахгүй (шалгалт эхлэхэд мөр хоосон гарна). */}
          {needsDirective && (
            <Form.Item
              name="directive_id"
              label="Удирдамж"
              rules={[{ required: true, message: "Удирдамжаа сонгоно уу" }]}
              extra={
                directives.length
                  ? "Шалгах асуудал нь энэ удирдамжаас бүрдэнэ"
                  : "Удирдамж олдсонгүй — Лавлах сан → «Хяналтын удирдамж» хэсэгт үүсгэнэ үү"
              }
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={
                  directives.length ? "Удирдамж сонгох" : "Удирдамж алга"
                }
                notFoundContent="Мөр бүхий удирдамж бүртгэгдээгүй байна"
                // Ноорог удирдамжийг НУУХГҮЙ, харин СОНГУУЛАХГҮЙ — «яагаад
                // алга вэ» гэж эргэлзэхээс сайн. Батлуулмагц идэвхжинэ.
                options={directives.map((d) => ({
                  value: d.id,
                  disabled: d.status === "draft",
                  label: `${d.directive_no ? "№" + d.directive_no + " · " : ""}${
                    d.name
                  } (${d.item_count} мөр${
                    d.is_planned ? "" : " · төлөвлөгөөт бус"
                  }${d.level_code === "bn" ? " · БН-ийн" : ""}${
                    d.status === "draft" ? " · НООРОГ, батлагдаагүй" : ""
                  })`,
                }))}
              />
            </Form.Item>
          )}

          {!needsDirective && templates.length > 1 && (
            <Form.Item
              name="template_id"
              label="Шалгах хуудас"
              rules={[{ required: true, message: "Шалгах хуудсаа сонгоно уу" }]}
              extra="Аль мэргэжилтний хуудсаар шалгаж байна вэ"
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Хуудас сонгох"
                options={(() => {
                  const fmt = (t) => ({
                    value: t.id,
                    label: `${t.name} (${t.item_count ?? 0} үзүүлэлт)`,
                  });
                  const mine = templates.filter((t) => myIds.includes(t.id));
                  const rest = templates.filter((t) => !myIds.includes(t.id));
                  // Надад хамаарах хуудсыг ДЭЭД талд тусад нь
                  if (!mine.length) return templates.map(fmt);
                  return [
                    { label: "Миний хариуцах", options: mine.map(fmt) },
                    { label: "Бусад", options: rest.map(fmt) },
                  ];
                })()}
              />
            </Form.Item>
          )}

          {deptFixed ? (
            // Маягтад сонгогдсон нэгжийг зөвхөн харуулна (дахин асуухгүй)
            <div
              style={{
                background: "#F6F8FB",
                border: "1px solid #E6E9EF",
                borderRadius: 8,
                padding: "8px 12px",
                marginBottom: 12,
                fontSize: 13,
              }}
            >
              <b>Бүтцийн нэгж:</b> {bn.name || bn.code}
              {section.name ? ` · ${section.name}` : ""}
            </div>
          ) : (
            <>
            {/* ⚠ I·II ШАТАНД нэгжийг ЗӨВХӨН ХАРУУЛНА (2026-10-29).

                ⚠ Сонгогчдыг УСТГААГҮЙ, `display: none`-ээр нуув —
                  ТЭД ӨӨРСДӨӨ өөрийн цех/хэсгийг АВТОМАТААР сонгодог
                  (`departmentTseh` нь `!seesAll` үед өөрийн цехийг,
                  `departmentNegj` нь `autoSelectOwn !== false` үед
                  өөрийн хэсгийг; сүүлийнх нь жагсаалтад байхгүй бол
                  өөрийн хэсгийг НЭМЖ Ч оруулдаг). Устгавал тэр авто-
                  сонголт ажиллахгүй болж `bn.code` хоосон үлдэх ба
                  «Бүтцийн нэгжээ сонгоно уу» гэж гацна.

                ⚠ КОДЫГ ӨӨРӨӨ БОДООГҮЙ. `user.info.negj_code`-оос
                  шууд авах нь буруу: хэмжихэд 664 хэрэглэгчийн 23-ын
                  `negj_code` нь 1 тэмдэгт (хуурамч «0»), 101-ийн нь
                  ГРУППИЙН түвшин (6 орон) байв. Сонгогч нь лавлахтай
                  тулгаж зөв түвшнийг олдог тул түүнд даатгав.

                ⚠ ГРУППИЙГ НУУГААГҮЙ — хэрэглэгчийн шаардлага нь
                  «БН, хэсэг 2-г» нуух; групп нь хэсэг дотор хийгддэг
                  шалгалтыг заадаг тул сонгогдох ёстой. */}
            {stage12 ? (
              <div
                style={{
                  background: "#F6F8FB",
                  border: "1px solid #E6E9EF",
                  borderRadius: 8,
                  padding: "8px 12px",
                  marginBottom: 12,
                  fontSize: 13,
                }}
              >
                <b>Бүтцийн нэгж:</b>{" "}
                {bn.name || bn.code || "тодорхойлж байна…"}
                {section.name || section.code
                  ? ` · ${section.name || section.code}`
                  : ""}
              </div>
            ) : null}
            <Row gutter={12}>
              {/* ⚠ УТГА ТОДОРСОН ҮЕД Л НУУНА. Авто-сонголт унавал
                  (нэгж лавлахад байхгүй, сүлжээ унасан) сонгогч
                  ХАРАГДАЖ, хэрэглэгч гараар сонгоно. Нөхцөлгүй нуувал
                  `bn.code` хоосон үлдэж, «Бүтцийн нэгжээ сонгоно уу»
                  гэсэн мессежтэй ГАЦНА — шалтгаан нь нуугдсан тул
                  хэрэглэгч гарц олохгүй. */}
              <Col
                xs={24}
                sm={12}
                style={stage12 && bn.code ? { display: "none" } : undefined}
              >
                {/* ⚠ `required` нь ЗӨВХӨН улаан одыг гаргана — валидаци
                    нэмэхгүй. Энэ талбар нь `Form`-ын талбар БИШ
                    (`DepartmentTseh` нь өөрийн төлөвтэй) тул `rules`
                    хэрэглэж болохгүй: шалгалт нь `doCreate` дотор
                    гараар явдаг («Бүтцийн нэгжээ сонгоно уу»). */}
                <Form.Item label="Бүтцийн нэгж (цех)" required>
                  <DepartmentTseh
                    value={bn.code || undefined}
                    /* ⚠ ШАТААС ХАМААРСАН ХАМРАХ ХҮРЭЭ (2026-10-15)
                       4 дүгээр шатны хяналт үйлдвэрийн БҮХ нэгжид
                       явагддаг тул тэр шат сонгогдсон үед сонгогч
                       бүх нэгжийг татна. 1-3 дугаар шат нь нэгжийн
                       ДОТООД хяналт — тэр үед зөвхөн өөрийн нэгж.
                       `key` нь шат солигдоход сонгогчийг дахин
                       байгуулж, дотоод `list` төлөв хуучин хүрээнээс
                       үлдэхээс сэргийлнэ. */
                    key={`bn-${stageCode}`}
                    /* ⚠ Шатыг ҮРГЭЛЖ дамжуулна, зөвхөн "IV"-ийг биш:
                       `stage` нь сонгогчид «энэ дуудагч шат мэднэ»
                       гэдгийг илтгэх тэмдэг бөгөөд IV→II шилжилтэд
                       хүчингүй болсон сонголтыг буцаахад хэрэгтэй.
                       Backend нь "IV"-ээс бусдыг тоохгүй. */
                    stage={stageCode || "I"}
                    onChange={(code, parentId, name) => {
                      setBn({ code: code || "", name: name || "", parentId });
                      setSection({ code: "", name: "" });
                      // Хэсэг цэвэрлэгдвэл групп нь тодорхойгүй болно
                      setGrp({ code: "", name: "", id: null });
                      /* ⚠ Хариуцагчийг ЦЭВЭРЛЭХГҮЙ. ХАБЭА-н инженер өөр
                         цехэд шалгалт хийх нь ХЭВИЙН (журам 2.8.3), тиймээс
                         нэгж солих болгонд өөрийгөө дахин сонгуулах нь
                         илүүц. Өөр хүнийг заах бол гараар сольж болно. */
                      // Шинэ нэгжийн тоог дахин бөглөнө
                      setCountAuto(true);
                    }}
                  />
                </Form.Item>
              </Col>
              {/* ⚠ ХЭМЖСЭН (2026-10-29): 1-2 шатны 664 хэрэглэгчийн 23-ын
                  `negj_code` нь «0» — тэд цехийн түвшинд ШУУД харьяалагддаг
                  (цех 68·69·31·99 ба 01xx хэлтсүүд) тул авто-сонголт
                  хийх хэсэг БАЙХГҮЙ. Тэдэнд сонгогч харагдана. */}
              <Col
                xs={24}
                sm={12}
                style={
                  stage12 && section.code ? { display: "none" } : undefined
                }
              >
                <Form.Item label="Хэсэг">
                  <DepartmentNegj
                    key={`${bn.code || "x"}-${bn.parentId || "no-bn"}`}
                    value={section.code || undefined}
                    paranetid={bn.parentId}
                    /* ⚠ parentCode ЗААВАЛ. Зөвхөн paranetid өгвөл
                       parentid нь суулгацад хоосон/зөрүүтэй үед хэсгийн
                       жагсаалт ХООСОН гардаг, мөн хэрэглэгчийн ӨӨРИЙН
                       хэсэг (negj_code) жагсаалтад нэмэгдэхгүй, авто-
                       сонгогдохгүй байв (2026-09-22). Кодын угтвараар
                       (25 → 2501…, 0104 → 010401…) найдвартай холбоно.
                       Анхан шатны (I) шалгагч бүр цех + хэсэгтэй байх
                       ёстой — хэсгээс шалгах хуудас нь хамаарна. */
                    parentCode={bn.code}
                    /* ⚠ Цехийн сонгогчтой ИЖИЛ хамрах хүрээ. Эс бөгөөс
                       IV шатанд өөр цех сонгоход тэр цехийн хэсгүүд
                       хэрэглэгчийн хүрээнд байхгүй тул жагсаалт
                       хоосон гарна. */
                    stage={stageCode || "I"}
                    onChange={(code, name) => {
                      setSection({ code: code || "", name: name || "" });
                      /* ⚠ Хэсэг солигдоход ГРУППИЙГ ЦЭВЭРЛЭНЭ. Эс бөгөөс
                         өмнөх хэсгийн групп үлдэж, өөр хэсгийн шалгалтад
                         хамааралгүй групп бичигдэнэ (тайлан гуйвна). */
                      setGrp((g) =>
                        groupBelongsToSection(g.code, code)
                          ? g
                          // ⚠ `id`-г Ч цэвэрлэнэ: үлдвэл групп арилсан
                          //   атал ажилтны тоо тэр группээр хумигдсан
                          //   хэвээр үлдэнэ.
                          : { code: "", name: "", id: null },
                      );
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Групп">
                  {/* ⚠ ЗААВАЛ БИШ — 208 хэсгийн зөвхөн 24-д групп байдаг.
                      Группгүй хэсэгт сонгогч «Групп байхгүй» гэж харагдана
                      (нуувал хэрэглэгч шалтгааныг эрэлхийлнэ). */}
                  <DepartmentGroup
                    key={`grp-${section.code || "x"}`}
                    value={grp.code || undefined}
                    parentCode={section.code}
                    stage={stageCode || "I"}
                    /* ⚠ 3 ДАХЬ АРГУМЕНТ (бүтэн мөр) — `id` хэрэгтэй:
                       ажилтан ГРУППИЙН түвшинд КОДООР бүртгэгддэггүй
                       (лавлахын группийн кодоор хайвал 0 гарна),
                       холбоос нь ЗӨВХӨН `departmentid` дээр. Үүнгүйгээр
                       ажилтны тоо группээр хумигдахгүй — хэсгийнх
                       гарсаар байв (2026-10-27-ны гомдол). */
                    onChange={(code, name, row) =>
                      setGrp({
                        code: code || "",
                        name: name || "",
                        id: row?.id ?? null,
                      })
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            </>
          )}

          {/* ⚠ GPS нь `area_location`-оос ТУСДАА: тэр нь ажлын байрны
              НЭР (лавлахтай тулгагддаг), энэ нь координат. Хольбол
              лавлахтай тулгах боломжгүй болно. */}
          <Form.Item label="Байршил (GPS)" style={{ marginBottom: 12 }}>
            <Space wrap>
              <Button
                size="small"
                icon={<EnvironmentOutlined />}
                loading={gpsState === "loading"}
                onClick={grabGps}
              >
                {gps ? "Дахин авах" : "Байршил авах"}
              </Button>
              {gps ? (
                <>
                  <Tag color="green">
                    {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}
                  </Tag>
                  {gps.accuracy ? (
                    <span style={{ fontSize: 11, color: "#8C8B85" }}>
                      ±{Math.round(gps.accuracy)} м
                    </span>
                  ) : null}
                  <Button
                    size="small"
                    type="link"
                    danger
                    onClick={() => {
                      setGps(null);
                      setGpsState("idle");
                    }}
                  >
                    Цэвэрлэх
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => setManual((v) => !v)}
                  >
                    Гараар оруулах
                  </Button>
                  <span style={{ fontSize: 11, color: "#8C8B85" }}>
                    Заавал биш
                  </span>
                </>
              )}
            </Space>
            {gpsErr && !gps
              ? (() => {
                  /* ⚠ ТАНИГДААГҮЙ шалтгаанд ч УНАХГҮЙ: `GPS_HELP[x]`
                     undefined бол `.title` нь бүх модалыг цагаан
                     дэлгэц болгоно. Ирээдүйд шинэ код нэмэхэд
                     хамгаалалт болно. */
                  const h = GPS_HELP[gpsErr] || GPS_HELP.unsupported;
                  return (
                    <Alert
                      type={gpsErr === "denied" ? "warning" : "info"}
                      showIcon
                      style={{ marginTop: 8 }}
                      message={h.title}
                      description={
                        <div style={{ fontSize: 12 }}>
                          {h.steps.map((t, i) => (
                            <div key={i}>
                              {h.steps.length > 1 ? `${i + 1}. ` : ""}
                              {t}
                            </div>
                          ))}
                        </div>
                      }
                    />
                  );
                })()
              : null}
            {manual && !gps ? (
              <div style={{ marginTop: 8 }}>
                <Space.Compact style={{ width: "100%", maxWidth: 420 }}>
                  <AutoComplete
                    style={{ width: "100%" }}
                    value={manualText}
                    onChange={setManualText}
                    options={[]}
                    placeholder="47.92123, 106.91234"
                  />
                  <Button type="primary" onClick={applyManual}>
                    Тавих
                  </Button>
                </Space.Compact>
                <div style={{ fontSize: 11, color: "#8C8B85", marginTop: 4 }}>
                  Google Maps дээр цэг дээр баруун товч дарж координатыг
                  хуулаад энд тавина.
                </div>
              </div>
            ) : null}
          </Form.Item>

          <Form.Item
            name="area_location"
            label="Шалгасан ажлын байр / байршил"
            extra={
              bn.code && !workplaceOptions.length
                ? "Энэ нэгжид ажлын байр бүртгэгдээгүй байна — Лавлах сан → «БН-ийн ажлын байр» дээр бүртгэвэл эндээс сонгогдоно."
                : "Лавлахаас сонгоно уу. Байхгүй бол шинээр бичиж болно."
            }
          >
            <AutoComplete
              options={workplaceOptions}
              placeholder="Ажлын байрны нэр"
              filterOption={(input, option) =>
                String(option?.value || "")
                  .toLowerCase()
                  .includes(String(input || "").toLowerCase())
              }
              // Лавлахаас сонгосон бол id-г хадгална; гараар бичвэл null —
              // ингэснээр «лавлахад орсон» ба «чөлөөт бичвэр» хоёр ялгагдана.
              onSelect={(_v, opt) => setWorkplaceId(opt?.id ?? null)}
              onChange={(v) => {
                const hit = workplaceOptions.find((o) => o.value === v);
                setWorkplaceId(hit?.id ?? null);
              }}
              allowClear
            />
          </Form.Item>

          {/* ── ШАЛГАСАН АЖЛЫН БАЙРУУДЫН ЧЕКЛИСТ (2026-10-29) ────────
              Хэрэглэгч: «Шалгасан ажлын байруудыг чек листээр харуулж
              зэрэг хяналтын хуудас эхлүүлж, хэрвээ зөрчилгүй бол бүгд
              бүртгэгдэж, хэрвээ аль нэгд нь зөрчил илэрвэл тухайн
              зөрчлийн хуудас дээрээ ажлын байраа сонгож бүртгэх».

              ⚠ ЗӨРЧЛИЙН ТАЛ АЛЬ ХЭДИЙН БЭЛЭН: зөрчлийн маягт дээр
                «Зөрчил гарсан ажлын байр» сонгогч бий бөгөөд сервер нь
                `inspection_item_result.workplace_id`-д хадгалдаг
                (`apply_workplace`). Тиймээс энд ЗӨВХӨН үзсэн байруудыг
                бүртгэх хэсгийг нэмэв.

              ⚠ Жагсаалт нь ХЭСЭГ · ГРУПП-ийн шүүлттэй ижил (нэг
                `workplaceRows`) — сонгогчоос өөр зүйл харуулбал
                хэрэглэгч эвдэрсэн гэж бодно.

              ⚠ ХООСОН бол ОГТ ХАРУУЛАХГҮЙ: 56 цехийн 46-д ажлын байр
                бүртгэгдээгүй (233 мөр 10 цехэд — хэмжсэн 2026-10-29)
                тул хоосон чеклист нь тэднийг зөвхөн тээнэгэлзүүлнэ. */}
          {/* ⚠⚠ III ба IV ШАТАНД ЧЕКЛИСТ ХАРАГДАХГҮЙ (2026-10-30,
              хэрэглэгч: «3,4 дээр олон ажлын байранд үүсгэж
              болохгүй»).
              ЯАГААД: III шат нь БН-ийн КОМИСС бүхэл нэгжийг (журам
              2.5), IV шат нь ХАБЭА хэлтэс үйлдвэрийн хэмжээнд
              шалгадаг. Байр тутамд шалгалт үүсгэвэл нэг комиссын нэг
              шалгалт 10-20 бичлэг болж хуваагдаж, хуваарь · тайлан ·
              албан шаардлага бүгд давхардана.
              ХЭМЖСЭН (2026-10-30): яг ийм 20 шалгалт үүссэн байв —
              нэг хэрэглэгч 15:07-д 11 шалгалтыг нэг минутад, 15 нь
              бүрэн хоосон. Сервер ч одооноос 400 буцаана. */}
          {workplaceRows.length > 0 && !["III", "IV"].includes(stageCode) ? (
            <Form.Item
              label="Шалгасан ажлын байрууд (чеклист)"
              extra={
                wpFiltered.widened
                  ? "⚠ Энэ хэсэг/группт ажлын байр бүртгэгдээгүй тул ЦЕХИЙН бүх байрыг харуулав. Лавлах сан → «БН-ийн ажлын байр» дээр хэсгээ заавал холбоно уу."
                  : wpChecked.length > 1
                    ? `${wpChecked.length} байр сонгогдсон — байр тутамд ТУСДАА шалгалт үүснэ (${wpChecked.length} дугаар).`
                    : "Хэд хэдэн байрыг зэрэг үзсэн бол тэмдэглэнэ. Тэмдэглэвэл дээрх нэг байрны талбар хэрэглэгдэхгүй."
              }
              style={{ marginBottom: 12 }}
            >
              <div
                style={{
                  maxHeight: 190,
                  overflowY: "auto",
                  border: "1px solid #E6E9EF",
                  borderRadius: 8,
                  padding: "8px 10px",
                  background: "#FCFCFD",
                }}
              >
                <div style={{ marginBottom: 6 }}>
                  <Checkbox
                    indeterminate={
                      wpChecked.length > 0 &&
                      wpChecked.length < workplaceRows.length
                    }
                    checked={
                      workplaceRows.length > 0 &&
                      wpChecked.length === workplaceRows.length
                    }
                    onChange={(e) =>
                      setWpChecked(
                        e.target.checked
                          ? workplaceRows.map((w) => w.id)
                          : [],
                      )
                    }
                  >
                    <b style={{ fontSize: 12.5 }}>
                      Бүгдийг ({workplaceRows.length})
                    </b>
                  </Checkbox>
                </div>
                <Checkbox.Group
                  style={{ width: "100%" }}
                  value={wpChecked}
                  onChange={(vals) => setWpChecked(vals)}
                >
                  {workplaceRows.map((w) => (
                    <div key={w.id} style={{ padding: "2px 0" }}>
                      <Checkbox value={w.id}>
                        <span style={{ fontSize: 12.5 }}>{w.name}</span>
                        {w.is_high_risk ? (
                          <span
                            style={{
                              color: "#DC2626",
                              marginLeft: 6,
                              fontSize: 11,
                            }}
                          >
                            өндөр эрсдэлт
                          </span>
                        ) : null}
                        {w.code || w.grp_name ? (
                          <span
                            style={{
                              fontSize: 11,
                              color: "#8c8c8c",
                              marginLeft: 6,
                            }}
                          >
                            {[w.code, w.grp_name].filter(Boolean).join(" · ")}
                          </span>
                        ) : null}
                      </Checkbox>
                    </div>
                  ))}
                </Checkbox.Group>
              </div>
            </Form.Item>
          ) : null}
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="worker_count"
                label="Ажилтны тоо"
                extra={
                  countLoading
                    ? "Лавлахаас тоолж байна…"
                    : scopeCount != null
                      ? `Лавлахад ${scopeCount} ажилтан${
                          section.code ? " (сонгосон хэсэгт)" : " (бүх хэсэг)"
                        }${countAuto ? "" : " · гараар засагдсан"}`
                      : "Бүтцийн нэгжээ сонгоход автоматаар бөглөгдөнө"
                }
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  // Гараар засмагц автомат бөглөлт зогсоно — нэгж солиход
                  // хэрэглэгчийн оруулсан тоо дарагдахгүй
                  onChange={() => setCountAuto(false)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="inspection_date"
                label="Огноо"
                initialValue={moment()}
              >
                {/* Утсанд цаг сонгогч (3 багана) багтдаггүй — өдөр л сонгуулна,
                    цаг нь одоогийнхоороо үлдэнэ */}
                <DatePicker
                  style={{ width: "100%" }}
                  showTime={!isMobile}
                  inputReadOnly={isMobile}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* ХЯНАЛТ ХИЙХ АЖИЛТАН — сонгосон БН/хэсгийн ажилтнаас.
              ⚠ Жагсаалт нь `bnCode`/`tsehCode`-оор хумигдана тул өөр цехийн
                ажилтан санамсаргүй сонгогдохгүй (WorkerSelect → /xab/workers).
              ⚠ Хоосон үлдээвэл нэвтэрсэн хэрэглэгч бичигдэнэ — өмнөх зан
                төлөвтэй ижил. */}
          <Form.Item
            label="Хяналт хийх ажилтан (хариуцагч)"
            extra={
              inspector?.tn
                ? "Анхдагчаар та өөрөө. Өөр хүн шалгаж байгаа бол нэр/ТН-ээр хайж сольж болно."
                : "Нэр эсвэл ТН-ээр хайна. Хоосон бол өөрөө бүртгэгдэнэ."
            }
          >
            <WorkerSelect
              key={`${bn.code || "no-bn"}-${section.code || ""}`}
              size="middle"
              value={inspector?.tn}
              bnCode={bn.code || undefined}
              tsehCode={section.code || undefined}
              initialWorker={inspector}
              placeholder="Нэр эсвэл ТН-ээр хайх"
              onChange={(tn, w) => setInspector(tn ? w || { tn } : null)}
            />
          </Form.Item>

          {/* Журмын дагуу тухайн нэгжид МӨРДӨГДӨЖ буй хуудасны мэдээлэл —
              цонхны хамгийн доор. Өмнө нь "хувилбар 1 · 2026-08-04" гэсэн
              нүцгэн шошго байсныг аль хуудас, хэн баталсан, хэдэн асуулттай,
              хэдий хүртэл хүчинтэй зэрэг утга бүхий мэдээллээр дэлгэв. */}
          {bn.code && <SheetInfo sheet={sheet} bn={bn} section={section} />}
        </Form>
        </div>

        {/* ── Шалгах хуудас — НЭГ ЦОНХОНД доошоо үргэлжилнэ ──────────────
            Өмнө нь «Эхлүүлэх» дармагц ЭНЭ модал хаагдаж, өөр модал нээгддэг
            байсан. Хэрэглэгч сонголтоо (шат · нэгж · хуудас) харахаа болиод
            контекстээ алддаг байв. Одоо шалгалт үүсмэгц үзүүлэлтүүд шууд
            доор нь гарч ирнэ — нэг урсгал. */}
        {fillId && (
          <div
            style={{
              marginTop: 14,
              paddingTop: 14,
              borderTop: "2px solid #E6E9EF",
            }}
          >
            <InspectionChecklistForm
              inspectionId={fillId}
              // Энэ цонхонд сонгогдсон цех/хэсэг — хариуцагчийн жагсаалтыг
              // ШАЛГАЖ БУЙ нэгжээр хязгаарлана (шалгагчийнхаар биш).
              bnCode={bn.code || undefined}
              tsehCode={section.code || undefined}
              onClosed={() => {
                setFillId(null);
                setOpen(false);
                onFinished?.();
              }}
            />
          </div>
        )}
        </div>
      </Modal>
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 *  SheetInfo — тухайн нэгжид МӨРДӨГДӨЖ буй хяналтын хуудасны мэдээлэл.
 *
 *  Журам 2.3.2 / 2.4.2 / 2.5.3: нэгж бүр өөрийн онцлогт тохирсон хуудсаа
 *  боловсруулж, ХАБЭА-н зөвлөлөөр хэлэлцүүлэн, БН даргаар батлуулж, 1 жилийн
 *  хугацаатай мөрддөг. Шалгалт эхлүүлэхийн өмнө шалгагч нь ЯМАР хуудсаар
 *  бөглөхөө тодорхой мэдэх ёстой тул бүх түлхүүр мэдээллийг задалж үзүүлнэ.
 * ──────────────────────────────────────────────────────────────────────── */
const fmtDay = (v) => (v ? String(v).slice(0, 10) : "—");

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = moment(String(dateStr).slice(0, 10), "YYYY-MM-DD");
  if (!d.isValid()) return null;
  return d.diff(moment().startOf("day"), "days");
}

function InfoRow({ label, children }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
      <span
        style={{
          flex: "0 0 118px",
          fontSize: 11,
          fontWeight: 700,
          color: "#8A94A6",
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: "#1F2430" }}>
        {children}
      </span>
    </div>
  );
}

function SheetInfo({ sheet, bn, section }) {
  // ── Батлагдсан хуудасгүй — журмын үлгэрчилсэн загвараар бөглөнө ──────────
  if (!sheet) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginTop: 4 }}
        message="Энэ нэгжид батлагдсан хяналтын хуудас алга"
        description={
          /* ⚠ Inline фонт БИЧИХГҮЙ — глобал `compactAlert.css` бүх
             хуудсанд нэг хэмжээ тогтоодог; энд дарж бичвэл энэ мэдээлэл
             ганцаараа том харагдана (2026-09-24). */
          <div>
            Шалгалт нь <b>журмын үлгэрчилсэн загвараар</b> бөглөгдөнө.
            <br />
            Нэгжийн онцлогт тохируулсан хуудсыг «Лавлах» хэсэгт боловсруулж,
            ХАБЭА-н зөвлөлөөр хэлэлцүүлэн, БН даргаар батлуулна
            <span style={{ color: "#8A94A6" }}> (журам 2.3.2 / 2.4.2 / 2.5.3)</span>.
          </div>
        }
      />
    );
  }

  const left = daysUntil(sheet.valid_until);
  // Хүчинтэй хугацааны төлөв — дуусахад 30 хоног үлдсэнээс анхааруулна
  const expired = left != null && left < 0;
  const soon = left != null && left >= 0 && left <= 30;
  const accent = expired ? "#DC2626" : soon ? "#CA8A04" : "#16A34A";

  return (
    <div
      style={{
        marginTop: 4,
        border: "1px solid #E6E9EF",
        borderLeft: `4px solid ${accent}`,
        borderRadius: 10,
        background: "#FCFDFE",
        padding: "10px 14px",
      }}
    >
      {/* Толгой — юугаар бөглөхөө нэг мөрөнд */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 8,
        }}
      >
        <i
          className={
            "fas " + (expired ? "fa-exclamation-triangle" : "fa-check-circle")
          }
          style={{ color: accent }}
        />
        <b style={{ fontSize: 13, color: accent }}>
          Батлагдсан хяналтын хуудсаар бөглөнө
        </b>
        <Tag color={expired ? "red" : soon ? "orange" : "green"}>
          {expired
            ? "Хугацаа дууссан"
            : soon
              ? `Дуусахад ${left} хоног`
              : "Мөрдөгдөж буй"}
        </Tag>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <InfoRow label="Хуудас">
          {/* ⚠ ЦЕХ эхэлж, ХЭСЭГ нь араас (2026-10-26-ны гомдол: «III шат
              эхлүүлэхэд доор хөл дээр Систем ашиглалтын хэсэг гэж буруу
              гарч байна»).
              ШАЛТГААН: `xab.checklist_sheet` нь БҮХ МОДУЛЬТАЙ ИЖИЛ
              конвенцтэй — `tseh_*` = ЦЕХ (жш «60 · Цахилгаан цех»),
              `bn_*` = ХЭСЭГ («6034 · Телемеханикийн хэсэг»). Энд
              `bn_name`-ийг ЭХЭНД (цехийн байранд) бичсэн тул хэсгийн нэр
              цех мэт харагдаж байв. Нөөц утга нь ч солигдсон байсан:
              энэ компонентод `bn` = ЦЕХ, `section` = ХЭСЭГ. */}
          {(() => {
            const tsehTxt = sheet.tseh_name || bn?.name || bn?.code || "";
            const secTxt = sheet.bn_name || section?.name || "";
            return [tsehTxt, secTxt].filter(Boolean).join(" · ") || "—";
          })()}
          <Tag color="blue" style={{ marginLeft: 8 }}>
            Хувилбар {sheet.version}
          </Tag>
          {sheet.item_count != null && (
            <span style={{ marginLeft: 4, fontSize: 12, color: "#8A94A6" }}>
              · {sheet.item_count} асуулт
            </span>
          )}
        </InfoRow>

        <InfoRow label="Баталсан">
          {sheet.approved_by_name || "—"}
          <span style={{ color: "#8A94A6" }}>
            {" · "}
            {fmtDay(sheet.approved_date)}
          </span>
        </InfoRow>

        <InfoRow label="Хүчинтэй">
          <span style={{ color: accent, fontWeight: 600 }}>
            {fmtDay(sheet.valid_until)} хүртэл
          </span>
          {left != null && (
            <span style={{ color: "#8A94A6" }}>
              {expired
                ? ` · ${Math.abs(left)} хоногийн өмнө дууссан`
                : ` · ${left} хоног үлдсэн`}
            </span>
          )}
        </InfoRow>

        {sheet.council_by_name || sheet.council_date ? (
          <InfoRow label="ХАБЭА зөвлөл">
            {sheet.council_by_name || "—"}
            <span style={{ color: "#8A94A6" }}>
              {" · "}
              {fmtDay(sheet.council_date)}
            </span>
          </InfoRow>
        ) : null}
      </div>

      {expired && (
        <div style={{ fontSize: 12, color: "#DC2626", marginTop: 8 }}>
          Хуудасны 1 жилийн хугацаа дууссан байна — шинэ хувилбарыг батлуулна уу.
        </div>
      )}
    </div>
  );
}
